/* eslint-disable */
import { httpPut } from "../../../Utils/apis/apis";
import {
  applyTheme,
  applyMode,
  getStoredTheme,
  getStoredMode,
  applySidebarVibrant,
  getStoredSidebarVibrant,
  applySidebarOpen,
  getStoredSidebarOpen,
} from "../../Factory/Molecules/Common/Navigations/M_ThemeSelector/M_ThemeSelector.helper";
import {
  applyFont,
  getStoredFont,
} from "../../Factory/Molecules/Common/Navigations/M_FontSelector/M_FontSelector.helper";
import {
  applyDisplaySetting,
  getStoredValues,
} from "../../Factory/Molecules/Common/Navigations/M_DisplaySettings/M_DisplaySettings.helper";

// Property keys stored on the contact vertex (prefixed to avoid collisions with contact fields)
const PREF_KEYS = {
  theme: "pref_theme",
  mode: "pref_mode",
  fontFamily: "pref_fontFamily",
  fontScale: "pref_fontScale",
  lineHeight: "pref_lineHeight",
  radiusScale: "pref_radiusScale",
  vibrantSidebar: "pref_vibrantSidebar",
  sidebarOpen: "pref_sidebarOpen",
};

// Prevents the save listener from re-firing while we apply DB preferences
let _isApplyingFromDB = false;
let _saveTimer = null;

const getContactFromStorage = () => {
  try {
    const raw = localStorage.getItem("contact_crm");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.id ? parsed : null;
  } catch {
    return null;
  }
};

const collectCurrentPreferences = () => {
  const { fontScale, lineHeight, radiusScale } = getStoredValues();
  return {
    theme: getStoredTheme(),
    mode: getStoredMode(),
    fontFamily: getStoredFont(),
    fontScale,
    lineHeight,
    radiusScale,
    // Stored as a string so the boolean round-trips through the DB property
    vibrantSidebar: getStoredSidebarVibrant() ? "true" : "false",
    sidebarOpen: getStoredSidebarOpen() ? "true" : "false",
  };
};

// Debounced — saves all 6 preferences to the contact vertex after a 1 s idle
export const saveUserPreferences = () => {
  if (_isApplyingFromDB) return;
  const contact = getContactFromStorage();
  if (!contact) return;

  clearTimeout(_saveTimer);
  _saveTimer = setTimeout(() => {
    const prefs = collectCurrentPreferences();
    const properties = {};
    Object.entries(PREF_KEYS).forEach(([prefKey, dbKey]) => {
      properties[dbKey] = prefs[prefKey] || "";
    });

    httpPut("/CRMData/update", {
      id: contact.id,
      label: contact.label || "contact",
      type: "vertex",
      properties,
    }).catch(() => {
      // Silent fail — preferences still work from localStorage
    });
  }, 1000);
};

// Called after contact is fetched from API — applies any saved preferences to the UI
export const applyPreferencesFromContact = (contactProperties) => {
  if (!contactProperties) return;

  const theme = contactProperties[PREF_KEYS.theme];
  const mode = contactProperties[PREF_KEYS.mode];
  const fontFamily = contactProperties[PREF_KEYS.fontFamily];
  const fontScale = contactProperties[PREF_KEYS.fontScale];
  const lineHeight = contactProperties[PREF_KEYS.lineHeight];
  const radiusScale = contactProperties[PREF_KEYS.radiusScale];
  const vibrantSidebar = contactProperties[PREF_KEYS.vibrantSidebar];
  const sidebarOpen = contactProperties[PREF_KEYS.sidebarOpen];

  // Nothing saved yet — first login on this account, keep localStorage defaults
  if (
    !theme &&
    !mode &&
    !fontFamily &&
    !fontScale &&
    !lineHeight &&
    !radiusScale &&
    !vibrantSidebar &&
    !sidebarOpen
  )
    return;

  _isApplyingFromDB = true;
  try {
    if (theme) applyTheme(theme, null, true);
    if (mode) applyMode(mode, null, true);
    if (fontFamily) applyFont(fontFamily);
    // silent: true suppresses the displayPreferenceChange event during load
    if (fontScale)
      applyDisplaySetting("fontScale", fontScale, { silent: true });
    if (lineHeight)
      applyDisplaySetting("lineHeight", lineHeight, { silent: true });
    if (radiusScale)
      applyDisplaySetting("radiusScale", radiusScale, { silent: true });
    if (vibrantSidebar) applySidebarVibrant(vibrantSidebar === "true");
    if (sidebarOpen) applySidebarOpen(sidebarOpen === "true");
  } finally {
    _isApplyingFromDB = false;
  }
};

// Call once on app boot — listens to all preference-change events and persists them to DB
export const initPreferenceSync = () => {
  ["themeChange", "fontChange", "displayPreferenceChange"].forEach(
    (eventName) => {
      window.addEventListener(eventName, saveUserPreferences);
    }
  );
};
